import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, AlertCircle, TrendingUp, Target } from 'lucide-react';

interface SystemStatus {
  totalQuestions: number;
  targetQuestions: number;
  uniqueRoles: number;
  targetRoles: number;
  activeOperation: boolean;
  duplicateRoles: number;
  topRoles: Array<{ role: string; count: number }>;
}

export const SystemIntegrationStatus: React.FC = () => {
  const [status, setStatus] = useState<SystemStatus | null>(null);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      // Get total count
      const { count: totalQuestions } = await supabase
        .from('interview_questions')
        .select('*', { count: 'exact', head: true });

      // Get role distribution
      const { data: roleData } = await supabase
        .from('interview_questions')
        .select('role_type');

      const roleCounts: Record<string, number> = {};
      roleData?.forEach((item: any) => {
        roleCounts[item.role_type] = (roleCounts[item.role_type] || 0) + 1;
      });

      // Check for duplicate role types
      const duplicateRoles = Object.keys(roleCounts).filter(role => {
        const normalized = role.replace(/-/g, '');
        return Object.keys(roleCounts).some(other => 
          other !== role && other.replace(/-/g, '') === normalized
        );
      }).length;

      // Get top roles
      const topRoles = Object.entries(roleCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([role, count]) => ({ role, count }));

      // Check for active operations
      const { data: activeOps } = await supabase
        .from('question_generation_operations')
        .select('id')
        .eq('status', 'in_progress')
        .limit(1);

      setStatus({
        totalQuestions: totalQuestions || 0,
        targetQuestions: 100000,
        uniqueRoles: Object.keys(roleCounts).length,
        targetRoles: 64,
        activeOperation: (activeOps?.length || 0) > 0,
        duplicateRoles,
        topRoles
      });
    } catch (error) {
      console.error('Error fetching system status:', error);
    }
  };

  if (!status) return null;

  const progress = (status.totalQuestions / status.targetQuestions) * 100;
  const roleProgress = (status.uniqueRoles / status.targetRoles) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          System Integration Status
        </CardTitle>
        <CardDescription>
          Overview of the unified 100k question generation system
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Total Questions</span>
            <Badge variant={status.activeOperation ? 'default' : 'secondary'}>
              {status.activeOperation ? 'Generating' : 'Idle'}
            </Badge>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{status.totalQuestions.toLocaleString()} / {status.targetQuestions.toLocaleString()}</span>
            <span>{progress.toFixed(1)}%</span>
          </div>
          <Progress value={progress} className="h-3" />
        </div>

        {/* Role Coverage */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Role Coverage</span>
            {status.duplicateRoles > 0 ? (
              <Badge variant="destructive">
                <AlertCircle className="h-3 w-3 mr-1" />
                {status.duplicateRoles} duplicates
              </Badge>
            ) : (
              <Badge variant="default">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Clean
              </Badge>
            )}
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{status.uniqueRoles} / {status.targetRoles} roles</span>
            <span>{roleProgress.toFixed(1)}%</span>
          </div>
          <Progress value={roleProgress} className="h-3" />
        </div>

        {/* Top Roles */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <TrendingUp className="h-4 w-4" />
            Top 5 Roles by Question Count
          </div>
          <div className="space-y-1">
            {status.topRoles.map((item) => (
              <div key={item.role} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">
                  {item.role.replace(/-/g, ' ')}
                </span>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Duplicate Detection</div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Active</span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Auto Recovery</div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Active</span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Role Distribution</div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Balanced</span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Quality Prompts</div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Enhanced</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
